import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Modal,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  AppBar,
  Toolbar,
  Pagination,
  Badge,
  CircularProgress,
  styled,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddIcon from "@mui/icons-material/Add";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import LeftToolbar from "./LeftToolbar";
import NotificationSnackbar from "./NotificationBar";
import IppisSettings from "./IPPISSettingsPage";
import { alpha } from "@mui/material/styles";
import Footer from "./Footer";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const StatusBadge = styled("span")(({ status, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: "16px",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "capitalize",
  backgroundColor:
    status === "New"
      ? alpha(theme.palette.success.light, 0.2)
      : status === "Assigned"
        ? alpha(theme.palette.warning.light, 0.2)
        : status === "In Progress"
          ? alpha(theme.palette.info.light, 0.2)
          : alpha(theme.palette.grey[300], 0.2),
  color:
    status === "New"
      ? theme.palette.success.dark
      : status === "Assigned"
        ? theme.palette.warning.dark
        : status === "In Progress"
          ? theme.palette.info.dark
          : theme.palette.grey[800],
  "&:before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    marginRight: 6,
    backgroundColor:
      status === "New"
        ? theme.palette.success.main
        : status === "Assigned"
          ? theme.palette.warning.main
          : status === "In Progress"
            ? theme.palette.info.main
            : theme.palette.grey[500],
  },
}));

// Futuristic queue position badge, shown only for sender on pending queue
const QueueBadge = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 6,
  padding: "3px 10px",
  borderRadius: 14,
  fontSize: "0.7rem",
  letterSpacing: 0.2,
  color: theme.palette.primary.contrastText,
  background: "#3A9CEE",
  boxShadow: `0 0 12px ${alpha("#3A9CEE", 0.4)}`,
}));

// Futuristic info box for modal position message
const PositionInfoBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "12px 16px",
  borderRadius: 14,
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(135deg, ${alpha("#3A9CEE", 0.85)} 0%, ${alpha(
    theme.palette.primary.dark,
    0.85,
  )} 100%)`,
  border: `1px solid ${alpha("#3A9CEE", 0.35)}`,
  boxShadow: `0 6px 22px ${alpha("#3A9CEE", 0.35)}`,
}));

const PendingTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [response, setResponse] = useState("");
  const [assignee, setAssignee] = useState("");
  const [selectedMenu, setSelectedMenu] = useState("Pending Tickets");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [agents, setAgents] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState(null);
  const ticketsPerPage = 5;
  const [loadingTicketId, setLoadingTicketId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionLoading, setActionLoading] = useState({
    view: false,
    respond: false,
    assign: false,
  });

  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState({
    mode: "none",
    singleDate: "",
    startDate: "",
    endDate: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: "CreatedOn",
    direction: "asc",
  });

  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Employee search (Respond form)
  const [answerSearchTerm, setAnswerSearchTerm] = useState("");
  const [answerSearchType, setAnswerSearchType] = useState("idNumber");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState(null);
  const [showEmployeeData, setShowEmployeeData] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [isEditingEmployee, setIsEditingEmployee] = useState(false);
  const [editEmployee, setEditEmployee] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [isIppisSettingsOpen, setIsIppisSettingsOpen] = useState(false);
  const [editError, setEditError] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    request: null,
    response: null,
    error: null,
  });
  const entitySectorId = null;

  const handleEmployeeSearch = async () => {
    const term = answerSearchTerm.trim();
    if (!term) {
      setShowEmployeeData(false);
      setEmployeeData(null);
      setEmployeeList([]);
      return;
    }
    try {
      setEmployeeLoading(true);
      setEmployeeError(null);
      setIsEditingEmployee(false);
      setEmployeeData(null);

      const digits = term.replace(/\D/g, "");
      const detectedType = term.includes("@")
        ? "email"
        : digits.length === 16
          ? "idNumber"
          : digits.length === 10
            ? "phoneNumber"
            : /^\d+$/.test(term)
              ? "id"
              : /^[A-Za-z0-9-]+$/.test(term)
                ? "applicationNumber"
                : /^[A-Za-z]+(?:\s+[A-Za-z]+)?$/.test(term)
                  ? "lastName"
                  : "lastName";
      const res = await fetch("/api/support/find-employee-from-entity-sector", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entitySectorId, searchKeyword: term }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          typeof json === "string"
            ? json
            : (json && (json.message || json.Message)) || `HTTP ${res.status}`;
        setEmployeeError(msg);
        setShowEmployeeData(true);
        setEmployeeList([]);
        return;
      }
      const list = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : json
            ? [json]
            : [];

      if (list.length > 0) {
        setEmployeeList(list);
        setShowEmployeeData(true);
        setEmployeeError(null);
      } else {
        setEmployeeList([]);
        setShowEmployeeData(true);
        setEmployeeError("No record found");
      }
    } catch (err) {
      console.error("Employee search failed:", err);
      setEmployeeError("Search failed. Please try again.");
      setShowEmployeeData(true);
      setEmployeeList([]);
    } finally {
      setEmployeeLoading(false);
    }
  };

  const handleClearAnswerSearch = () => {
    setAnswerSearchTerm("");
    setEmployeeData(null);
    setEmployeeError(null);
    setShowEmployeeData(false);
    setIsEditingEmployee(false);
    setEmployeeList([]);
  };

  const startEmployeeEdit = (user) => {
    const u = user || employeeData;
    if (!u) return;
    setEditError(null);
    setEditEmployee({
      id: u.id ?? "",
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      idNumber: u.idNumber ?? "",
      applicationNumber: u.applicationNumber ?? "",
      phoneNumber: u.phoneNumber ?? "",
      email: u.email ?? "",
      dateOfBirth: u.dateOfBirth ?? "",
      positionName: u.positionName ?? "",
      entityName: u.entityName ?? "",
    });
    setIsEditingEmployee(true);
  };

  const cancelEmployeeEdit = () => {
    setIsEditingEmployee(false);
    setEditError(null);
  };

  const handleEmployeeUpdate = async () => {
    if (!editEmployee) return;
    setEditError(null);
    setDebugInfo({ request: null, response: null, error: null });

    const current = employeeData || {};
    const userId = editEmployee.id || current?.id;
    const idNumber = (editEmployee.idNumber || "").replace(/\s/g, "");
    const phoneNumber = editEmployee.phoneNumber || "";
    const email = editEmployee.email || "";

    const payload = {
      userId,
      idNumber: (current.idNumber || "").replace(/\s/g, ""),
      phoneNumber: current.phoneNumber || "",
      email: current.email || "",
    };
    let changed = false;
    if (idNumber && idNumber !== (current.idNumber || "")) {
      const oldPrefix = (current.idNumber || "")
        .replace(/\s/g, "")
        .slice(0, 13);
      const newPrefix = idNumber.slice(0, 13);
      if (oldPrefix && newPrefix && oldPrefix !== newPrefix) {
        const msg =
          "The first 13 characters of your NID Number can't be changed";
        setEditError(msg);
        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      payload.idNumber = idNumber;
      changed = true;
    }
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    if (phoneNumber && phoneNumber !== (current.phoneNumber || "")) {
      if (phoneDigits.length !== 10) {
        setEditError("Phone number must be 10 digits");
        return;
      }
      payload.phoneNumber = phoneNumber;
      changed = true;
    }
    if (email && email !== (current.email || "")) {
      payload.email = email;
      changed = true;
    }

    if (!changed) {
      setEditError("No changes to save");
      return;
    }

    try {
      setEditLoading(true);
      setDebugInfo((d) => ({ ...d, request: payload }));
      const res = await fetch("/api/support/update-profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          typeof data === "string"
            ? data
            : (data && (data.message || data.Message)) || `HTTP ${res.status}`;
        setDebugInfo((d) => ({ ...d, response: data, error: msg }));
        throw new Error(msg);
      }
      setDebugInfo((d) => ({ ...d, response: data }));
      const newId = payload.idNumber ?? current.idNumber;
      const newPhone = payload.phoneNumber ?? current.phoneNumber;
      const newEmail = payload.email ?? current.email;
      setEmployeeData((prev) => ({
        ...prev,
        idNumber: newId,
        phoneNumber: newPhone,
        email: newEmail,
      }));
      setEmployeeList((prev) =>
        Array.isArray(prev)
          ? prev.map((u) =>
            u?.id === userId
              ? {
                ...u,
                idNumber: newId,
                phoneNumber: newPhone,
                email: newEmail,
              }
              : u,
          )
          : prev,
      );
      setIsEditingEmployee(false);
      setSnackbarMessage(
        data?.message || "User information updated successfully",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Update failed:", err);
      const msg =
        typeof err?.message === "string" ? err.message : "Update failed";
      setEditError(msg);
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setEditLoading(false);
    }
  };
  const handleUnlockEmployee = async (employeeId) => {
    if (!employeeId) {
      setSnackbarMessage("Employee ID not found.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await fetch(
        `/api/support/unlock-employee/${employeeId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Failed to unlock employee");
      }

      setSnackbarMessage(data?.message || "Employee unlocked successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error unlocking employee:", error);
      setSnackbarMessage(
        error.message || "Failed to unlock employee. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await fetch("/api/support/tickets", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      setTickets(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setError("Failed to fetch tickets. Please try again later.");
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTickets();
  }, []);

  // Fetch full tickets for accurate totals and queue positions
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await fetch("/api/support/all-tickets", {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });
        const contentType = res.headers.get("content-type") || "";
        if (!res.ok) {
          const body = await res.text();
          console.error(
            "All-tickets fetch failed:",
            res.status,
            body?.slice(0, 200),
          );
          setAllTickets([]);
          return;
        }
        if (contentType.includes("application/json")) {
          const data = await res.json();
          setAllTickets(
            Array.isArray(data)
              ? data
              : Array.isArray(data?.tickets)
                ? data.tickets
                : [],
          );
        } else {
          const body = await res.text();
          console.error(
            "All-tickets returned non-JSON:",
            contentType,
            body?.slice(0, 200),
          );
          setAllTickets([]);
        }
      } catch (err) {
        console.error("Error fetching all tickets:", err);
        setAllTickets([]);
        // Non-blocking: keep the page operational even if this fails
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    fetch("/api/support/supportAgents", {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setAgents(data);
      })
      .catch((err) => {
        console.error("Error fetching support agents:", err);
      });
  }, []);

  const uniqueCategories = [
    ...new Set(tickets.map((ticket) => ticket.categoryName)),
  ];
  const highlightMatch = (text, term) => {
    if (!term || !text) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    return text
      .toString()
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part,
      );
  };

  const sortTickets = (tickets) => {
    if (!sortConfig.key) return tickets;
    return [...tickets].sort((a, b) => {
      if (sortConfig.key === "CreatedOn") {
        const rawA = a.CreatedOn ?? a.createdOn;
        const rawB = b.CreatedOn ?? b.createdOn;
        const dateA = rawA ? new Date(rawA).getTime() : 0;
        const dateB = rawB ? new Date(rawB).getTime() : 0;
        if (dateA < dateB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (dateA > dateB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Compute pending queue positions across all pending statuses (New, Assigned, In Progress)
  const normalizeStatus = (s) =>
    String(s ?? "")
      .trim()
      .toLowerCase();
  const pendingStatusesNorm = new Set([
    "new",
    "assigned",
    "in progress",
    "inprogress",
    "pending",
  ]);
  const sourceTicketsForQueue =
    (allTickets && allTickets.length ? allTickets : tickets) || [];
  const statusIdPendingSet = new Set([1, 2, 3]);
  const pendingSorted = sortTickets(
    sourceTicketsForQueue.filter((t) => {
      const norm = normalizeStatus(t?.TicketStatus ?? t?.Status);
      const byName = pendingStatusesNorm.has(norm);
      const byId = statusIdPendingSet.has(Number(t?.StatusId));
      return byName || byId;
    }),
  );
  const getId = (t) => String(t?.id ?? t?.Id ?? t?.TicketId);
  const positionById = Object.fromEntries(
    pendingSorted.map((t, i) => [getId(t), i + 1]),
  );
  const pendingTotal = pendingSorted.length;
  const isNonAdminSupport =
    user?.roleId !== "ADMIN" && user?.roleId !== "SUPPORT";
  const myPendingTicketsSorted = pendingSorted.filter(
    (t) => String(t?.UserId) === String(user?.id),
  );
  const totalTicketsAll =
    Array.isArray(allTickets) && allTickets.length
      ? allTickets.length
      : Array.isArray(tickets)
        ? tickets.length
        : 0;
  // Debug logs
  console.debug("[QueueDebug] totals", {
    ticketsLen: Array.isArray(tickets) ? tickets.length : 0,
    allTicketsLen: Array.isArray(allTickets) ? allTickets.length : 0,
    totalTicketsAll,
    pendingTotal,
    myPendingLen: myPendingTicketsSorted.length,
  });
  console.debug(
    "[QueueDebug] statuses sample",
    Array.from(
      new Set(sourceTicketsForQueue.map((t) => t?.TicketStatus ?? t?.Status)),
    ).slice(0, 20),
  );
  console.debug(
    "[QueueDebug] statusId sample",
    Array.from(new Set(sourceTicketsForQueue.map((t) => t?.StatusId))).slice(
      0,
      20,
    ),
  );
  console.debug(
    "[QueueDebug] id mapping sample",
    sourceTicketsForQueue
      .slice(0, 10)
      .map((t) => ({ rawId: t?.id, rawIdCaps: t?.Id, mapped: getId(t) })),
  );
  const shouldShowBadge = (ticket) => {
    const isOwner = String(ticket?.UserId) === String(user?.id);
    const pos =
      positionById[String(ticket?.id ?? ticket?.Id ?? ticket?.TicketId)];
    const allowed = isNonAdminSupport;
    const statusNorm = normalizeStatus(ticket?.TicketStatus ?? ticket?.Status);
    const isPending = pendingStatusesNorm.has(statusNorm);
    console.debug("[BadgeCheck]", {
      id: ticket?.id,
      isOwner,
      allowed,
      pos,
      status: statusNorm,
      isPending,
    });
    return allowed && isOwner && pos && isPending;
  };
  const filteredTickets = Array.isArray(tickets)
    ? sortTickets(
      tickets.filter((ticket) => {
        const matchesSearch =
          ticket.TicketDescription?.toLowerCase().includes(
            searchTerm.toLowerCase(),
          ) ||
          ticket.Sender?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ticket.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.InstitutionName?.toLowerCase().includes(
            searchTerm.toLowerCase(),
          );
        const matchesCategory =
          selectedCategory === "" || ticket.categoryName === selectedCategory;
        let matchesDate = true;
        if (dateFilter.mode === "single" && dateFilter.singleDate) {
          const ticketDate = new Date(ticket.CreatedOn)
            .toISOString()
            .split("T")[0];
          matchesDate = ticketDate === dateFilter.singleDate;
        } else if (dateFilter.mode === "range") {
          const ticketDate = new Date(ticket.CreatedOn).getTime();
          const startDate = dateFilter.startDate
            ? new Date(dateFilter.startDate).getTime()
            : 0;
          const endDate = dateFilter.endDate
            ? new Date(dateFilter.endDate).getTime() + 86400000
            : Infinity;
          matchesDate = ticketDate >= startDate && ticketDate < endDate;
        }
        const matchesRole =
          roleFilter === "" || ticket.UserRoleId === roleFilter;
        const matchesStatus =
          statusFilter === "" || ticket.TicketStatus === statusFilter;

        return (
          matchesSearch &&
          matchesCategory &&
          matchesDate &&
          matchesRole &&
          matchesStatus
        );
      }),
    )
    : [];
  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(
    indexOfFirstTicket,
    indexOfLastTicket,
  );

  const formatDateOnly = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });
  };

  // Modal handlers
  const handleOpenModal = async (type, ticket) => {
    setLoadingTicketId(ticket.id);
    if (type === "view" && ticket?.id) {
      const userToken = localStorage.getItem("userToken");
      if (!userToken) {
        handleSnackbarOpen("Unauthorized. Please login first.", "error");
        setLoadingTicketId(null);
        return;
      }

      if (
        (user.roleId === "ADMIN" || user.roleId === "SUPPORT") &&
        ticket.TicketStatus === "New"
      ) {
        try {
          await axios.put(
            `/api/support/open-ticket/${ticket.id}`,
            {},
            {
              withCredentials: true,
              headers: {
                Authorization: `${userToken}`,
              },
            },
          );
          handleSnackbarOpen("Ticket marked as opened", "success");
        } catch (error) {
          console.error("Failed to mark ticket as opened", error);
          handleSnackbarOpen("Failed to mark ticket as opened.", "error");
        }
        await fetchTickets();
      }
    }
    setSelectedTicket(ticket);
    setModalType(type);
    setLoadingTicketId(null);
  };

  const handleCloseModal = () => {
    setModalType(null);
    setResponse("");
    setAssignee("");
  };
  // Response handler
  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    setActionLoading((prev) => ({ ...prev, respond: true }));
    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      handleSnackbarOpen("Unauthorized. Please login first.", "error");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("ticketId", selectedTicket?.id);
    formDataToSend.append("responseMessage", response);
    try {
      const res = await fetch("/api/support/respond-ticket", {
        method: "POST",
        credentials: "include",
        body: formDataToSend,
        headers: {
          Authorization: `${userToken}`,
        },
      });
      const data = await res.json();
      if (!user?.id) {
        handleSnackbarOpen("No user found", "error");
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || "Failed to submit response.");
      }
      handleSnackbarOpen(
        data.message || "Response submitted successfully!",
        "success",
      );
      await fetchTickets();
      handleCloseModal();
    } catch (error) {
      console.error("Submit response error:", error);
      const message = error?.message || "An error occurred!";
      handleSnackbarOpen(message, "error");
    }
    setActionLoading((prev) => ({ ...prev, respond: false }));
  };

  // Assign ticket handler
  const handleAssignTicket = async (e) => {
    e.preventDefault();
    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      handleSnackbarOpen("Unauthorized. Please login first.", "error");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("ticketId", selectedTicket?.id);
    formDataToSend.append("personAssignedTo", assignee);
    formDataToSend.append("assignReason", response);

    try {
      const res = await fetch("/api/support/assign-ticket", {
        method: "POST",
        credentials: "include",
        body: formDataToSend,
        headers: {
          Authorization: `${userToken}`,
        },
      });
      const data = await res.json();
      if (!user?.id) {
        handleSnackbarOpen("No user found", "error");
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || "Failed to assign ticket.");
      }
      handleSnackbarOpen(data.message || "Assigned successfully!", "success");
      await fetchTickets();
      handleCloseModal();
    } catch (error) {
      console.error("Assigned error:", error);
      const message = error?.message || "An error occurred!";
      handleSnackbarOpen(message, "error");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (
      dateFilter.mode === "range" &&
      dateFilter.startDate &&
      dateFilter.endDate
    ) {
      const start = new Date(dateFilter.startDate);
      const end = new Date(dateFilter.endDate);

      if (start > end) {
        handleSnackbarOpen("End date cannot be before start date", "warning");
      }
    }
  }, [dateFilter.mode, dateFilter.startDate, dateFilter.endDate]);
  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>

          <IconButton color="inherit">
            <Badge
              badgeContent={
                filteredTickets.length > 999 ? "999+" : filteredTickets.length
              }
              color="error"
              max={9999}
              onClick={() => {
                if (user.roleId === "ADMIN" || user.roleId === "SUPPORT") {
                  navigate("/Dashboard");
                }
              }}
              sx={{
                cursor:
                  user.roleId === "ADMIN" || user.roleId === "SUPPORT"
                    ? "pointer"
                    : "not-allowed",
                opacity: user.roleId === "ADMIN" || user.roleId === "SUPPORT",
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {statusFilter && (
            <Chip
              label={`Status: ${statusFilter}`}
              onDelete={() => setStatusFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {selectedCategory && (
            <Chip
              label={`Module: ${selectedCategory}`}
              onDelete={() => setSelectedCategory("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {roleFilter && (
            <Chip
              label={`Role: ${roleFilter}`}
              onDelete={() => setRoleFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {dateFilter.mode === "single" && dateFilter.singleDate && (
            <Chip
              label={`Date: ${new Date(
                dateFilter.singleDate,
              ).toLocaleDateString()}`}
              onDelete={() => setDateFilter({ ...dateFilter, singleDate: "" })}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {dateFilter.mode === "range" &&
            (dateFilter.startDate || dateFilter.endDate) && (
              <Chip
                label={`Range: ${dateFilter.startDate
                  ? new Date(dateFilter.startDate).toLocaleDateString()
                  : "..."
                  } to ${dateFilter.endDate
                    ? new Date(dateFilter.endDate).toLocaleDateString()
                    : "..."
                  }`}
                onDelete={() =>
                  setDateFilter({ mode: "range", startDate: "", endDate: "" })
                }
                sx={{ borderRadius: "12px" }}
              />
            )}
        </Box>

        {/* Search and filter controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search tickets..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
   

          <Box
            sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}
          >
            {/* Date filter controls */}
            <Select
              value={dateFilter.mode}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, mode: e.target.value })
              }
              size="small"
              sx={{ minWidth: 120, borderRadius: "12px" }}
            >
              <MenuItem value="none">No date filter</MenuItem>
              <MenuItem value="single">Specific date</MenuItem>
              <MenuItem value="range">Date range</MenuItem>
            </Select>

            {dateFilter.mode === "single" && (
              <TextField
                type="date"
                size="small"
                value={dateFilter.singleDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, singleDate: e.target.value })
                }
                sx={{ borderRadius: "12px" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {dateFilter.mode === "range" && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography sx={{ alignSelf: "center" }}>to</Typography>
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            {/* Quick date range presets */}
            {dateFilter.mode === "range" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: yesterday.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastWeek = new Date(today);
                    lastWeek.setDate(lastWeek.getDate() - 7);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastWeek.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastMonth = new Date(today);
                    lastMonth.setMonth(lastMonth.getMonth() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastMonth.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 30 days
                </Button>
              </Box>
            )}

            {/* Clear date filters button */}
            {(dateFilter.mode === "single" || dateFilter.mode === "range") && (
              <Button
                variant="outlined"
                onClick={() =>
                  setDateFilter({
                    mode: "none",
                    singleDate: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                sx={{ borderRadius: "12px" }}
              >
                Clear
              </Button>
            )}

            {/* Role filter */}
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {[...new Set(tickets.map((ticket) => ticket.UserRoleId))].map(
                (role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ),
              )}
            </Select>

            {/* Status filter */}
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Assigned">Assigned</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
            </Select>

            {/* Category filter */}
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 200,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Modules</MenuItem>
              {uniqueCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Tickets table */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pending Tickets
            </Typography>
            
            {!["ADMIN", "SUPPORT"].includes(user?.roleId) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/CreateTicket")}
                startIcon={<AddIcon />}
                sx={{
                  fontWeight: 600,
                  borderRadius: "999px",
                  textTransform: "none",
                  bgcolor: theme.palette.primary.light,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Request new support
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              {filteredTickets.length}{" "}
              {filteredTickets.length === 1 ? "ticket" : "tickets"} found
            </Typography>
          </Box>

          {/* Global position card for sender */}
          {/* {isNonAdminSupport && myPendingTicketsSorted.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.25)} 0%, ${alpha(theme.palette.primary.light, 0.25)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {myPendingTicketsSorted.map((t) => (
                <Tooltip
                  key={t.id}
                  title={`Your ticket is #${positionById[getId(t)]} of ${pendingTotal} in the pending queue — awaiting processing.`}
                  arrow
                  placement="top"
                >
                  <QueueBadge>
                    #{positionById[getId(t)]} of {pendingTotal}
                  </QueueBadge>
                </Tooltip>
              ))}
            </Box>
          )} */}

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                >
                  {[
                    { id: "id", label: "Id" },
                    { id: "TicketDescription", label: "Description" },
                    { id: "Sender", label: "Sender" },
                    { id: "InstitutionName", label: "Institution" },
                    { id: "TicketStatus", label: "Status" },
                    { id: "categoryName", label: "Module" },
                    { id: "UserRoleId", label: "Role" },
                    { id: "CreatedOn", label: "Date" },
                    { id: "actions", label: "Actions" },
                  ].map((column) => (
                    <ModernTableCell
                      key={column.id}
                      onClick={() => {
                        if (column.id !== "actions") {
                          let direction = "desc";
                          if (sortConfig.key === column.id) {
                            direction =
                              sortConfig.direction === "desc" ? "asc" : "desc";
                          }
                          setSortConfig({ key: column.id, direction });
                        }
                      }}
                      sx={{
                        cursor: column.id !== "actions" ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            column.id !== "actions"
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "inherit",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {column.label}
                        {sortConfig.key === column.id && (
                          <Box ml={1} display="flex" flexDirection="column">
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "asc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▲
                            </span>
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "desc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▼
                            </span>
                          </Box>
                        )}
                      </Box>
                    </ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading tickets...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentTickets.length > 0 ? (
                  currentTickets.map((ticket) => (
                    <StyledTableRow key={ticket.id} hover>
                      <ModernTableCell>
                        <Box display="flex" flexDirection="column">
                          <span>{ticket.id ?? ticket.Id}</span>
                          {shouldShowBadge(ticket) ? (
                            <Tooltip
                              title={`Your ticket number is #${positionById[getId(ticket)]
                                } out of  ${pendingTotal} 
                              in the pending requests.Thank you for your patience while we work to address all requests as soon as we can`}
                              arrow
                              placement="top"
                            >
                              <QueueBadge>
                                #{positionById[getId(ticket)]} of {pendingTotal}
                              </QueueBadge>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        {isMobile
                          ? highlightMatch(
                            ticket.TicketDescription.substring(0, 20),
                            searchTerm,
                          )
                          : ticket.TicketDescription.length > 30
                            ? highlightMatch(
                              ticket.TicketDescription.substring(0, 27),
                              searchTerm,
                            )
                            : highlightMatch(
                              ticket.TicketDescription,
                              searchTerm,
                            )}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(ticket.Sender, searchTerm)}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(ticket.InstitutionName, searchTerm)}
                      </ModernTableCell>
                      <ModernTableCell>
                        <Tooltip
                          title={
                            ticket.openedBy
                              ? user.roleId === "SUPPORT" ||
                                user.roleId === "HR" ||
                                user.roleId === "DAF" ||
                                user.roleId === "CBM" ||
                                user.roleId === "ADMIN"
                                ? `${ticket.openedBy} is currently working on it`
                                : "IPPIS TEAM is currently working on it"
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <StatusBadge status={ticket.TicketStatus}>
                              {ticket.TicketStatus}
                            </StatusBadge>
                          </span>
                        </Tooltip>
                      </ModernTableCell>
                      <ModernTableCell>{ticket.categoryName}</ModernTableCell>
                      <ModernTableCell>{ticket.UserRoleId}</ModernTableCell>
                      <ModernTableCell>
                        <Tooltip title={formatDateForDisplay(ticket.CreatedOn)}>
                          <Box>
                            <Box
                              fontWeight={
                                sortConfig.key === "CreatedOn" ? 600 : 400
                              }
                            >
                              {formatDateForDisplay(ticket.CreatedOn)}
                            </Box>
                            <Box fontSize="0.75rem" color="text.secondary">
                              {formatDateForDisplay(ticket.CreatedOn)}
                            </Box>
                          </Box>
                        </Tooltip>
                      </ModernTableCell>
                      <ModernTableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenModal("view", ticket)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1,
                              ),
                            },
                          }}
                          disabled={loadingTicketId === ticket.id}
                        >
                          {loadingTicketId === ticket.id ? (
                            <CircularProgress size={24} />
                          ) : (
                            <VisibilityIcon
                              fontSize={isMobile ? "small" : "medium"}
                            />
                          )}
                        </IconButton>
                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1">No tickets found</Typography>
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredTickets.length / ticketsPerPage)}
              page={currentPage}
              onChange={(e, value) => setCurrentPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>
      </MainContent>

      <Footer collapsed={collapsed} />

      {/* response modal */}
      <Modal
        open={modalType !== null}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 900, md: 950 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            p: 0,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#bdbdbd",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#9e9e9e",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              p: 3,
              borderRadius: "12px 12px 0 0",
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 600,
                pr: 5,
              }}
            >
              Ticket Details
            </Typography>
          </Box>

          {/* Modal Body */}
          <Box sx={{ p: 3 }}>
            {/* Ticket Information */}
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={1.5}>
                {[
                  {
                    label: "UserId",
                    value: selectedTicket?.UserId,
                    color: "#06b6d4",
                  },
                  {
                    label: "Name",
                    value: selectedTicket?.Sender,
                    color: "#3b82f6",
                  },

                  {
                    label: "Phone",
                    value: selectedTicket?.PhoneNumber,
                    color: "#10b981",
                  },

                  {
                    label: "Email",
                    value: selectedTicket?.Email,
                    color: "#ec4899",
                  },
                  {
                    label: "ID Number",
                    value: selectedTicket?.IdNumber,
                    color: "#f59e0b",
                  },
                  {
                    label: "Institution",
                    value: selectedTicket?.InstitutionName,
                    color: "#8b5cf6",
                  },
                ].map((field, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: 1.5,
                        p: 1.5,
                        border: "1px solid rgba(0, 0, 0, 0.15)",
                        transition: "all 0.2s",
                        height: "100%",
                        "&:hover": {
                          borderColor: field.color,
                          boxShadow: `0 2px 8px ${field.color}20`,
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: field.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: "#000",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {field.label}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.primary",
                          fontWeight: 500,
                          mt: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={field.value || "N/A"}
                      >
                        {field.value || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.95rem",
                }}
              >
                Description:
              </Typography>
              <Typography
                sx={{
                  color: "text.primary",
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                }}
              >
                {selectedTicket?.TicketDescription || "No description provided"}
              </Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.95rem",
                }}
              >
                Attachment:
              </Typography>

              {/* Flex container for attachment + settings button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Left side: attachment */}
                {selectedTicket?.AttachmentRefPath ? (
                  <Button
                    href={`https://documents.ippis.rw/preview/${selectedTicket.AttachmentRefPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    View Attachment
                  </Button>
                ) : (
                  <Chip
                    label="No attachment available"
                    size="small"
                    sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1) }}
                  />
                )}

                {/* Conditionally render IPPIS SETTINGS button */}
                {(user?.roleId === "ADMIN" || user?.roleId === "SUPPORT") && (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => setIsIppisSettingsOpen(true)}
                      startIcon={<SettingsIcon />}
                      sx={{
                        borderRadius: 50,
                        textTransform: "none",
                        px: 4,
                        py: 1,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                          transform: "scale(1.03)",
                        },
                      }}
                    >
                      IPPIS SETTINGS
                    </Button>

                    <IppisSettings
                      open={isIppisSettingsOpen}
                      onClose={() => setIsIppisSettingsOpen(false)}
                    />
                  </>
                )}
              </Box>
            </Box>

            {selectedTicket &&
              typeof positionById?.[getId(selectedTicket)] !== "undefined" ? (
              <PositionInfoBox sx={{ mb: 3 }}>
                <ConfirmationNumberIcon sx={{ fontSize: 22, opacity: 0.9 }} />
                <Typography sx={{ fontSize: "0.92rem", lineHeight: 1.55 }}>
                  {`Your ticket number is #${positionById[getId(selectedTicket)]
                    } out of  ${pendingTotal} in the pending requests.Thank you for your patience while we work to address all requests as soon as we can.`}
                </Typography>
              </PositionInfoBox>
            ) : null}

            {selectedTicket?.openedBy && (
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  mb: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.info.dark,
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                >
                  {user.roleId === "ADMIN" ||
                    user.roleId === "SUPPORT" ||
                    user.roleId === "HR" ||
                    user.roleId === "DAF" ||
                    user.roleId === "CBM"
                    ? `${selectedTicket.openedBy} is currently working on it`
                    : "IPPIS TEAM is currently working on it"}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Action Buttons - Show only when not in answer/assign mode */}
            {modalType === "view" && (
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                {!(
                  selectedTicket?.TicketStatus === "Assigned" ||
                  selectedTicket?.UserId === user?.id
                ) && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleOpenModal("answer", selectedTicket)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        boxShadow: 2,
                        "&:hover": {
                          boxShadow: 4,
                        },
                      }}
                    >
                      Respond
                    </Button>
                  )}
                {selectedTicket &&
                  !(
                    selectedTicket.UserId === user?.id ||
                    selectedTicket.TicketStatus === "Assigned"
                  ) && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleOpenModal("assign", selectedTicket)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        boxShadow: 2,
                        "&:hover": {
                          boxShadow: 4,
                        },
                      }}
                    >
                      Assign
                    </Button>
                  )}
              </Box>
            )}

            {/* Response Form - Visible when answer button is clicked */}
            {modalType === "answer" && (
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                  p: 3,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.success.dark,
                      fontWeight: 600,
                      fontSize: "1.1rem",
                    }}
                  >
                    Respond to Ticket
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      placeholder="Search employee..."
                      size="small"
                      value={answerSearchTerm}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAnswerSearchTerm(v);
                        if (!v.trim()) setShowEmployeeData(false);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {answerSearchTerm && (
                              <IconButton
                                aria-label="clear"
                                size="small"
                                onClick={handleClearAnswerSearch}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 240 }}
                    />
                    <Button
                      variant="contained"
                      color="success"
                      disableElevation
                      startIcon={
                        employeeLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SearchIcon />
                        )
                      }
                      onClick={handleEmployeeSearch}
                      disabled={employeeLoading}
                      sx={{
                        textTransform: "none",
                        borderRadius: 999,
                        px: 2.5,
                        py: 0.75,
                        letterSpacing: 0.4,
                        backgroundColor: "success.main",
                        boxShadow:
                          "0 4px 12px rgba(76,175,80,0.3), 0 0 6px rgba(27,94,32,0.25)",
                        transition: "all 180ms ease",
                        "&:hover": {
                          boxShadow:
                            "0 6px 16px rgba(76,175,80,0.5), 0 0 12px rgba(27,94,32,0.4)",
                          transform: "translateY(-1px)",
                        },

                        "&.Mui-disabled": {
                          opacity: 0.85,
                        },
                      }}
                    >
                      {employeeLoading ? "Searching..." : "Search"}
                    </Button>
                  </Box>
                </Box>
                <Collapse
                  in={!showEmployeeData}
                  timeout={theme.transitions.duration.standard}
                >
                  <Typography
                    sx={{
                      mb: 2,
                      color: "text.secondary",
                      fontSize: "0.9rem",
                    }}
                  >
                    Provide your response to this ticket. Your message will be
                    sent to {selectedTicket?.Sender}.
                  </Typography>
                  <TextField
                    label="Your Response"
                    fullWidth
                    multiline
                    rows={5}
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Type your detailed response here..."
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      },
                    }}
                  />
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenModal("view", selectedTicket)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        px: 3,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        px: 3,
                        boxShadow: 2,
                        "&:hover": {
                          boxShadow: 4,
                        },
                      }}
                      onClick={handleSubmitResponse}
                      disabled={!response || actionLoading.respond}
                    >
                      {actionLoading.respond ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Submit Response"
                      )}
                    </Button>
                  </Box>
                </Collapse>
                <Collapse
                  in={showEmployeeData && !isEditingEmployee}
                  timeout={theme.transitions.duration.standard}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.2,
                      )}`,
                      mt: 2,
                      overflow: "hidden",
                    }}
                  >
                    {employeeLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={28} />
                      </Box>
                    ) : employeeError ? (
                      <Typography color="error" sx={{ fontSize: "0.9rem" }}>
                        {employeeError}
                      </Typography>
                    ) : employeeList && employeeList.length > 0 ? (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.success.dark,
                            }}
                          >
                            Employee Results ({employeeList.length})
                          </Typography>
                        </Box>

                        <TableContainer
                          sx={{
                            maxHeight: 500,
                            "&::-webkit-scrollbar": {
                              width: "8px",
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: alpha(
                                theme.palette.success.main,
                                0.3,
                              ),
                              borderRadius: "4px",
                            },
                          }}
                        >
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  ID
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Name
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  IdNumber
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Phone
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Email
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Position
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Employment Date
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  End Date
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Institution
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.1,
                                    ),
                                    color: theme.palette.success.dark,
                                  }}
                                >
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {employeeList.map((u, index) => (
                                <TableRow
                                  key={
                                    u.id ||
                                    `${u.firstName}-${u.lastName}-${u.idNumber}`
                                  }
                                  sx={{
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.success.main,
                                        0.08,
                                      ),
                                    },
                                    bgcolor:
                                      index % 2 === 0
                                        ? "transparent"
                                        : alpha(
                                          theme.palette.success.main,
                                          0.02,
                                        ),
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    #{u.id ?? "-"}
                                  </TableCell>
                                  <TableCell>
                                    {`${u.lastName || ""} ${u.firstName || ""
                                      }`.trim() || "-"}
                                  </TableCell>
                                  <TableCell>{u.idNumber || "-"}</TableCell>

                                  <TableCell>{u.phoneNumber || "-"}</TableCell>
                                  <TableCell>{u.email || "-"}</TableCell>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      {u.positionName || "-"}
                                      {u.isSecondary && (
                                        <Chip
                                          label="Secondary"
                                          size="small"
                                          sx={{
                                            backgroundColor: "#1976d2",
                                            color: "#fff",
                                            fontWeight: 500,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {u.startDate
                                      ? formatDateOnly(u.startDate)
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {u.endDate
                                      ? formatDateOnly(u.endDate)
                                      : "Present"}
                                  </TableCell>

                                  <TableCell>{u.entityName || "-"}</TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={
                                        u.isAvailable
                                          ? "In Service"
                                          : "Not in Service"
                                      }
                                      sx={{
                                        backgroundColor: u.isAvailable
                                          ? "#158E3B"
                                          : "#A20204",
                                        color: "#fff",
                                        fontWeight: 600,
                                      }}
                                      size="small"
                                    />
                                  </TableCell>

                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => startEmployeeEdit(u)}
                                      sx={{
                                        color: theme.palette.success.main,
                                        "&:hover": {
                                          bgcolor: alpha(
                                            theme.palette.success.main,
                                            0.1,
                                          ),
                                        },
                                      }}
                                    >
                                      <MoreHorizIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : (
                      <Typography
                        sx={{ color: "text.secondary", fontSize: "0.9rem" }}
                      >
                        No results found.
                      </Typography>
                    )}
                  </Paper>
                </Collapse>
                <Collapse
                  in={showEmployeeData && isEditingEmployee}
                  timeout={theme.transitions.duration.standard}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.04),
                      border: `1px solid ${alpha(
                        theme.palette.info.main,
                        0.2,
                      )}`,
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, color: theme.palette.info.dark }}
                      >
                        Edit Employee:{editEmployee.id || ""}
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleUnlockEmployee(editEmployee.id)}
                          disabled={editEmployee.lockLevel === null}
                          sx={{
                            color:
                              editEmployee.lockLevel !== null
                                ? theme.palette.error.main
                                : theme.palette.success.main,

                            border: `1.5px solid ${editEmployee.lockLevel !== null
                              ? alpha(theme.palette.error.main, 0.5)
                              : alpha(theme.palette.success.main, 0.5)
                              }`,

                            padding: "8px",
                            transition: "all 0.2s ease-in-out",

                            "&:hover": {
                              bgcolor:
                                editEmployee.lockLevel !== null
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : alpha(theme.palette.success.main, 0.1),
                              borderColor:
                                editEmployee.lockLevel !== null
                                  ? theme.palette.error.main
                                  : theme.palette.success.main,
                              transform: "scale(1.05)",
                            },
                            "&.Mui-disabled": {
                              color: theme.palette.success.main,
                              borderColor: alpha(
                                theme.palette.success.main,
                                0.4,
                              ),
                              cursor: "not-allowed",
                              opacity: 0.6,
                            },
                          }}
                          title={
                            editEmployee.lockLevel !== null
                              ? "Unlock Employee (Currently Locked)"
                              : "Employee Already Unlocked"
                          }
                        >
                          <LockOpenIcon fontSize="small" />
                        </IconButton>

                        <IconButton size="small" onClick={cancelEmployeeEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="ID Number"
                          fullWidth
                          size="small"
                          value={editEmployee.idNumber || ""}
                          onChange={(e) =>
                            setEditEmployee((s) => ({
                              ...s,
                              idNumber: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Full Name"
                          fullWidth
                          size="small"
                          value={`${editEmployee.lastName || ""} ${editEmployee.firstName || ""
                            }`.trim()}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Date of Birth"
                          fullWidth
                          size="small"
                          value={
                            editEmployee.dateOfBirth
                              ? new Date(
                                editEmployee.dateOfBirth,
                              ).toLocaleDateString()
                              : ""
                          }
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Position"
                          fullWidth
                          size="small"
                          value={editEmployee.positionName || ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Entity"
                          fullWidth
                          size="small"
                          value={editEmployee.entityName || ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Application Number"
                          fullWidth
                          size="small"
                          value={editEmployee.applicationNumber || ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Phone"
                          fullWidth
                          size="small"
                          value={editEmployee.phoneNumber || ""}
                          onChange={(e) =>
                            setEditEmployee((s) => ({
                              ...s,
                              phoneNumber: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Email"
                          fullWidth
                          size="small"
                          value={editEmployee.email || ""}
                          onChange={(e) =>
                            setEditEmployee((s) => ({
                              ...s,
                              email: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <Button
                        onClick={cancelEmployeeEdit}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={handleEmployeeUpdate}
                        disabled={editLoading}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        {editLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </Box>
                    {/* <Collapse
                      in={debugOpen}
                      timeout={theme.transitions.duration.standard}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.warning.main, 0.05),
                          border: `1px solid ${alpha(
                            theme.palette.warning.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          Debug Details
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1 }}
                          color="text.secondary"
                        >
                          Shows the request payload, server response, and any
                          error message.
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.common.black, 0.04),
                            fontSize: "0.8rem",
                            maxHeight: 240,
                            overflow: "auto",
                          }}
                        >
                          {JSON.stringify(debugInfo, null, 2)}
                        </Box>
                      </Paper>
                    </Collapse> */}
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Assign Form - Visible when assign button is clicked */}
            {modalType === "assign" && (
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  p: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.dark,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  Assign Ticket
                </Typography>
                <Typography
                  sx={{
                    mb: 3,
                    color: "text.secondary",
                    fontSize: "0.9rem",
                  }}
                >
                  Select an agent and provide a reason for assigning this
                  ticket.
                </Typography>
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: "0.9rem",
                  }}
                >
                  Select Agent
                </Typography>
                <Select
                  fullWidth
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  displayEmpty
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Choose an agent from the list
                  </MenuItem>
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.agentId}>
                      {agent.SupportAgent}
                    </MenuItem>
                  ))}
                </Select>

                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: "0.9rem",
                  }}
                >
                  Reason for Assignment
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Explain why you're assigning this ticket to the selected agent..."
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    },
                  }}
                />
                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenModal("view", selectedTicket)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 3,
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                      },
                    }}
                    onClick={handleAssignTicket}
                    disabled={!assignee || !response || actionLoading.assign}
                  >
                    {actionLoading.assign ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Assign Ticket"
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
};

export default PendingTickets;
